import React, { useCallback, useEffect, useState } from 'react';
import crmClient from './crmClient';
import ContactDetail from './ContactDetail';
import { formatDate } from './format';

const emptyForm = { first_name: '', last_name: '', email: '', phone: '', title: '', contact_types: ['prospect'] };

const ContactsView = ({ meta }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [pagination, setPagination] = useState({ total: 0, limit: 100, offset: 0 });
  const [selectedContact, setSelectedContact] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    (offset = 0) => {
      setLoading(true);
      const params = { limit: 100, offset };
      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;
      if (stateFilter) params.state = stateFilter;
      crmClient
        .listContacts(params)
        .then(payload => {
          setContacts((payload && payload.contacts) || []);
          setPagination((payload && payload.pagination) || { total: 0, limit: 100, offset });
          setLoading(false);
        })
        .catch(() => {
          setError('Unable to load contacts.');
          setLoading(false);
        });
    },
    [search, typeFilter, stateFilter]
  );

  useEffect(() => {
    load();
  }, [load]);

  const contactTypes = (meta && meta.contact_types) || ['prospect', 'merchant', 'driver', 'investor'];
  const states = (meta && meta.states) || [];

  const toggleType = type =>
    setForm(prev => {
      const has = prev.contact_types.includes(type);
      const next = has ? prev.contact_types.filter(t => t !== type) : [...prev.contact_types, type];
      return { ...prev, contact_types: next.length ? next : ['prospect'] };
    });

  const submit = () => {
    setBusy(true);
    setError(null);
    crmClient
      .createContact(form)
      .then(() => {
        setBusy(false);
        setForm(emptyForm);
        setShowForm(false);
        load();
      })
      .catch(err => {
        setBusy(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Unable to create contact.');
      });
  };

  return (
    <div className={`crm-contacts${selectedContact ? ' crm-companies--split' : ''}`}>
      <div className="crm-companies-main">
      <div className="crm-filters">
        <input type="search" placeholder="Search contacts…" value={search} onChange={event => setSearch(event.target.value)} />
        <select value={typeFilter} onChange={event => setTypeFilter(event.target.value)}>
          <option value="">All types</option>
          {contactTypes.map(type => (
            <option key={type} value={type}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </option>
          ))}
        </select>
        <select value={stateFilter} onChange={event => setStateFilter(event.target.value)}>
          <option value="">All regions</option>
          {states.map(st => (
            <option key={st} value={st}>
              {st}
            </option>
          ))}
        </select>
        <button type="button" className="crm-btn" onClick={() => setShowForm(prev => !prev)}>
          {showForm ? 'Cancel' : 'Add contact'}
        </button>
      </div>

      {error && <div className="crm-inline-error">{error}</div>}

      {showForm && (
        <div className="crm-card crm-form">
          <div className="crm-field-row">
            <label>
              <span>First name</span>
              <input value={form.first_name} onChange={event => setForm({ ...form, first_name: event.target.value })} />
            </label>
            <label>
              <span>Last name</span>
              <input value={form.last_name} onChange={event => setForm({ ...form, last_name: event.target.value })} />
            </label>
          </div>
          <div className="crm-field-row">
            <label>
              <span>Email</span>
              <input type="email" value={form.email} onChange={event => setForm({ ...form, email: event.target.value })} />
            </label>
            <label>
              <span>Phone</span>
              <input value={form.phone} onChange={event => setForm({ ...form, phone: event.target.value })} />
            </label>
            <label>
              <span>Title</span>
              <input value={form.title} onChange={event => setForm({ ...form, title: event.target.value })} />
            </label>
          </div>
          <div className="crm-chip-row">
            {contactTypes.map(type => (
              <button
                type="button"
                key={type}
                className={`crm-chip${form.contact_types.includes(type) ? ' crm-chip--on' : ''}`}
                onClick={() => toggleType(type)}
              >
                {type}
              </button>
            ))}
          </div>
          <button type="button" className="crm-btn" disabled={busy || (!form.first_name && !form.email)} onClick={submit}>
            Save contact
          </button>
        </div>
      )}

      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Email</th>
              <th>Phone</th>
              <th className="crm-num">Added</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="crm-empty">
                  Loading contacts…
                </td>
              </tr>
            )}
            {!loading && contacts.length === 0 && (
              <tr>
                <td colSpan={5} className="crm-empty">
                  No contacts yet.
                </td>
              </tr>
            )}
            {!loading &&
              contacts.map(contact => (
                <tr
                  key={contact.crm_contact_id}
                  className={selectedContact && selectedContact.crm_contact_id === contact.crm_contact_id ? 'crm-row-active' : ''}
                  onClick={() => setSelectedContact(contact)}
                >
                  <td>
                    <strong>{[contact.first_name, contact.last_name].filter(Boolean).join(' ') || '—'}</strong>
                  </td>
                  <td>{(contact.contact_types || []).join(', ')}</td>
                  <td>{contact.email || '—'}</td>
                  <td>{contact.phone || '—'}</td>
                  <td className="crm-num">{formatDate(contact.created_at)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="crm-pagination">
        <span>
          {pagination.total
            ? `${pagination.offset + 1}–${Math.min(pagination.offset + pagination.limit, pagination.total)} of ${pagination.total.toLocaleString('en-US')}`
            : '0 contacts'}
        </span>
        <div>
          <button
            type="button"
            className="crm-btn crm-btn--ghost"
            disabled={pagination.offset <= 0}
            onClick={() => load(Math.max(pagination.offset - pagination.limit, 0))}
          >
            Previous
          </button>
          <button
            type="button"
            className="crm-btn crm-btn--ghost"
            disabled={pagination.offset + pagination.limit >= pagination.total}
            onClick={() => load(pagination.offset + pagination.limit)}
          >
            Next
          </button>
        </div>
      </div>
      </div>

      {selectedContact && (
        <ContactDetail
          contact={selectedContact}
          meta={meta}
          onClose={() => setSelectedContact(null)}
          onChanged={() => load(pagination.offset)}
        />
      )}
    </div>
  );
};

export default ContactsView;
